// @flow strict

import React from "react";
import { imgSrc } from "~common/utils";
import { playersMap } from "~robin-hood/utils";
import type { Player } from "~robin-hood/utils";
import type {
  RegisteredStore,
  Equipo,
  PokerValue,
  LocalPoker,
  Card,
  CardSuit,
  CardValue,
} from "~robin-hood/redux/store";
import {
  getCardSuit,
  getCardValue,
  asCard,
  equipoWithPrueba,
  localStoreWithPrueba,
} from "~robin-hood/redux/store";
import classnames from "~common/classnames";
import window from "~robin-hood/animations/window.module.css";
import styles from "./PruebaPoker.module.css";

const POKER_IMG_PLAYER = playersMap([
  imgSrc("/images/robin-hood/prueba-poker/POKER 1.png"),
  imgSrc("/images/robin-hood/prueba-poker/POKER 2.png"),
  imgSrc("/images/robin-hood/prueba-poker/POKER 3.png"),
  imgSrc("/images/robin-hood/prueba-poker/POKER 4.png"),
  imgSrc("/images/robin-hood/prueba-poker/POKER 5.png"),
  imgSrc("/images/robin-hood/prueba-poker/POKER 6.png"),
]);

const POKER_SECRET: PokerValue = [
  asCard("7", "corazones"),
  asCard("Q", "treboles"),
  asCard("3", "rombos"),
];

type Props = {
  player: Player,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
  store: RegisteredStore,
  updateLocalStore: (store: RegisteredStore) => void,
};

export function PruebaPoker(props: Props): React$Node {
  const { player, equipo, updateEquipo, store, updateLocalStore } = props;
  return (
    <div className={classnames(styles.pruebaPokerWrapper, window.scaleInBl)}>
      <div className={classnames(styles.pruebaPoker)}>
        <img
          alt="Poker"
          className={styles.image}
          src={POKER_IMG_PLAYER.get(player)}
        />
      </div>
      <Solucion
        player={player}
        equipo={equipo}
        updateEquipo={updateEquipo}
        store={store}
        updateLocalStore={updateLocalStore}
      />
    </div>
  );
}

type SolucionProps = {
  player: Player,
  store: RegisteredStore,
  updateLocalStore: (RegisteredStore) => void,
  equipo: Equipo,
  updateEquipo: (Equipo) => void,
};

class Solucion extends React.PureComponent<SolucionProps> {
  _doCheck = () => {
    const { store, equipo, updateEquipo, player } = this.props;
    const values = valueFromLocal(store, player);
    const isCorrect = correctFromEquipo(equipo);
    const correct = values.every((v, idx) => {
      return POKER_SECRET[idx] === v;
    });
    if (correct && correct !== isCorrect) {
      const updated = equipoWithPrueba(equipo, {
        poker: { value: values, correct },
      });
      updateEquipo(updated);
    }
    return correct;
  };

  // On change we won't connect with server
  _onCardChange = (cardIdx: 0 | 1 | 2): ((Card) => void) => {
    return (card: Card) => {
      const { store, updateLocalStore, player } = this.props;
      const values = valueFromLocal(store, player);
      if (values[cardIdx] === card) {
        return;
      }
      const newValues: PokerValue = [...values];
      newValues[cardIdx] = card;
      updateLocalStore(
        updateLocalWithPoker(store, player, { value: newValues })
      );
    };
  };

  render(): React$Node {
    const { equipo, store, player } = this.props;
    const correct = correctFromEquipo(equipo);
    const values = correct
      ? valueFromEquipo(equipo)
      : valueFromLocal(store, player);
    return (
      <div className={styles.solucion}>
        <SolucionCard
          correct={correct}
          defaultValue={values[0]}
          onChange={this._onCardChange(0)}
        />
        <SolucionCard
          correct={correct}
          defaultValue={values[1]}
          onChange={this._onCardChange(1)}
        />
        <SolucionCard
          correct={correct}
          defaultValue={values[2]}
          onChange={this._onCardChange(2)}
        />
        <CheckPoker correct={correct} onCheck={this._doCheck} />
      </div>
    );
  }
}

type CheckPokerProps = {
  correct: boolean,
  onCheck: () => boolean,
};
type CheckProperState = {
  checking: boolean,
  checked: boolean,
  result?: boolean,
};
class CheckPoker extends React.Component<CheckPokerProps, CheckProperState> {
  _timeout: ?TimeoutID;
  state: CheckProperState = { checking: false, checked: this.props.correct };

  componentWillUnmount() {
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = undefined;
    }
  }

  _onClick = () => {
    if (!this.props.correct && !this.state.checked && !this.state.checking) {
      if (this._timeout) {
        clearTimeout(this._timeout);
        this._timeout = undefined;
        this.setState({ checking: false });
      } else {
        this.setState({ checking: true });
        this._timeout = setTimeout(() => {
          if (this._timeout) {
            const correct = this.props.onCheck();
            this.setState({ result: correct, checking: false, checked: true });
            if (correct) {
              this._timeout = undefined;
            } else {
              this._timeout = setTimeout(() => {
                if (this._timeout) {
                  this.setState({
                    checked: this.props.correct,
                    result: undefined,
                  });
                }
                this._timeout = undefined;
              }, 3000);
            }
          }
        }, 3000);
      }
    }
  };

  render() {
    const { correct } = this.props;
    const { checking, checked, result } = this.state;
    return (
      <div
        className={classnames(styles.flipCard, checking && styles.checking)}
        onClick={this._onClick}
      >
        <div className={styles.flipCardInner}>
          <div className={styles.flipCardFront}>
            {!checking && <span className="fa fa-question"></span>}
          </div>
          <div
            className={classnames(
              styles.flipCardFinal,
              checked && styles.checked,
              correct || result ? styles.correct : styles.error
            )}
          ></div>
        </div>
      </div>
    );
  }
}

type CardProps = {
  correct: boolean,
  defaultValue: Card,
  onChange: (Card) => void,
};

function SolucionCard(props: CardProps) {
  const [card, updateCard] = React.useState(props.defaultValue);
  const cardSuit = getCardSuit(card);
  const isRedSuit = cardSuit === "rombos" || cardSuit === "corazones";
  return (
    <div
      className={classnames(styles.solucionCard, isRedSuit && styles.redSuit)}
    >
      <span
        className={styles.cardValue}
        disabled={props.correct}
        onClick={() => {
          if (props.correct) {
            return;
          }
          const value = nextValue(card);
          const newCard: Card = asCard(value, getCardSuit(card));
          props.onChange && props.onChange(newCard);
          updateCard((c) => asCard(value, getCardSuit(c)));
        }}
      >
        {renderValue(getCardValue(card))}
      </span>
      <span
        className={styles.cardSuit}
        disabled={props.correct}
        onClick={() => {
          if (props.correct) {
            return;
          }
          const suit = nextSuit(card);
          const newCard: Card = asCard(getCardValue(card), suit);
          props.onChange && props.onChange(newCard);
          updateCard((c) => asCard(getCardValue(c), suit));
        }}
      >
        {renderSuit(getCardSuit(card))}
      </span>
    </div>
  );
}

function renderSuit(suit: CardSuit): React$Node {
  switch (suit) {
    case "rombos":
      return "♦";
    case "corazones":
      return "♥";
    case "picas":
      return "♠";
    default:
      /*:: (suit: "treboles"); */
      return "♣";
  }
}

function renderValue(value: CardValue): React$Node {
  switch (value) {
    case "2":
      return 2;
    case "3":
      return 3;
    case "4":
      return 4;
    case "5":
      return 5;
    case "6":
      return 6;
    case "7":
      return 7;
    case "8":
      return 8;
    case "9":
      return 9;
    case "10":
      return 10;
    case "J":
      return "J";
    case "Q":
      return "Q";
    case "K":
      return "K";
    default:
      /*:: (value: "A"); */
      return "A";
  }
}

function nextSuit(value: Card): CardSuit {
  const suit = getCardSuit(value);
  switch (suit) {
    case "rombos":
      return "corazones";
    case "corazones":
      return "picas";
    case "picas":
      return "treboles";
    default:
      /*:: (suit: "treboles"); */
      return "rombos";
  }
}

function nextValue(card: Card): CardValue {
  const value = getCardValue(card);
  switch (value) {
    case "2":
      return "3";
    case "3":
      return "4";
    case "4":
      return "5";
    case "5":
      return "6";
    case "6":
      return "7";
    case "7":
      return "8";
    case "8":
      return "9";
    case "9":
      return "10";
    case "10":
      return "J";
    case "J":
      return "Q";
    case "Q":
      return "K";
    case "K":
      return "A";
    default:
      /*:: (value: "A"); */
      return "2";
  }
}

const DEFAULT_POKER: PokerValue = [
  asCard("2", "rombos"),
  asCard("2", "rombos"),
  asCard("2", "rombos"),
];

function valueFromLocal(store: RegisteredStore, player: Player): PokerValue {
  const poker = store.local?.poker;
  return (poker && poker[player] && poker[player].value) || DEFAULT_POKER;
}

function valueFromEquipo(equipo: Equipo): PokerValue {
  return equipo.pruebas?.poker?.value || DEFAULT_POKER;
}
function correctFromEquipo(equipo: Equipo): boolean {
  return equipo.pruebas?.poker?.correct || false;
}

function updateLocalWithPoker(
  store: RegisteredStore,
  player: Player,
  value: LocalPoker
): RegisteredStore {
  return localStoreWithPrueba(store, "poker", {
    [(player: string)]: value,
  });
}
